'use client';

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowUpDown,
  ArrowUpRight,
  BookOpen,
  Clock3,
  LayoutGrid,
  LineChart,
  MousePointerClick,
  RefreshCw,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Table2,
} from 'lucide-react';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Resize, Sort } from '@syncfusion/ej2-react-grids';
import { AppLink } from '@/components/mui/app-link';

type Row = {
  id: string;
  idCem: string;
  name: string;
  titleType: string;
  remunerationType: string;
  indexer: string;
  cashFlowType: string;
  riskFactor: string;
  taxRegime: string;
  liquidity: string;
  quoteDate: string | null;
  conversionDate: string | null;
  maturityDate: string | null;
  sellRateMorning: number | null;
  sellPuMorning: number | null;
  daysToMaturity: number;
  durationYears: number | null;
  modifiedDurationYears: number | null;
  dv01: number | null;
  convexity: number | null;
  source: string;
  sourceAsOf: string | null;
};

type Response = {
  data: Row[];
  benchmarks: {
    selic: BenchmarkPoint;
    ipca: BenchmarkPoint;
    igpm: BenchmarkPoint;
  };
  options: {
    titleTypes: string[];
    indexers: string[];
    cashFlowTypes: string[];
    remunerationTypes: string[];
    riskFactors: string[];
    taxRegimes: string[];
    liquidities: string[];
  };
};

type BenchmarkPoint = { value: number; date: string | null } | null;

type ViewMode = 'table' | 'cards' | 'charts';

const TITLE_LABELS: Record<string, string> = {
  PREFIXADO: 'Prefixado',
  'PREFIXADO COM JUROS SEMESTRAIS': 'Prefixado com juros semestrais',
  SELIC: 'Selic',
  SELIC_SPREAD: 'Spread sobre Selic',
  'IPCA+': 'IPCA+',
  'IPCA+ COM JUROS SEMESTRAIS': 'IPCA+ com juros semestrais',
  'IGPM+ COM JUROS SEMESTRAIS': 'IGP-M+ com juros semestrais',
  'RENDA+ APOSENTADORIA EXTRA': 'Renda+ aposentadoria extra',
  'EDUCA+': 'Educa+',
};

const LABELS: Record<string, string> = {
  NONE: 'Sem indexador',
  IPCA: 'IPCA',
  IGPM: 'IGP-M',
  SELIC: 'Selic',
  FIXED_RATE: 'Taxa fixa',
  FLOATING_RATE: 'Taxa flutuante',
  INDEX_PLUS_SPREAD: 'Índice + taxa',
  BULLET: 'Sem cupons',
  PERIODIC_COUPON: 'Juros semestrais',
  ANNUITY: 'Renda mensal',
  MATURITY: 'No vencimento',
  SEMIANNUAL: 'Semestral',
  MONTHLY: 'Mensal',
  NOMINAL_RATE: 'Taxa nominal',
  REAL_RATE: 'Taxa real',
  IGPM_RATE: 'Taxa IGP-M',
  REGRESSIVE_IR_IOF: 'Regressiva',
  EXEMPT: 'Isenta',
  TREASURY_DAILY_REPURCHASE: 'D+0 / D+1 útil',
  tesouro_transparente: 'Tesouro Transparente',
};

const defaultMaturityFrom = new Date().toISOString().slice(0, 10);
const formatDate = (value: string | null) =>
  value ? new Intl.DateTimeFormat('pt-BR', { timeZone: 'UTC' }).format(new Date(`${value}T12:00:00Z`)) : '—';

const formatNumber = (value: number | null, digits = 2) =>
  value === null ? '—' : value.toLocaleString('pt-BR', { maximumFractionDigits: digits });

const formatPrice = (value: number | null) =>
  value === null ? '—' : value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatEnum = (value: string | null | undefined) =>
  value ? (LABELS[value] ?? value.replaceAll('_', ' ').toLowerCase()) : '—';

const titleLabel = (value: string) => TITLE_LABELS[value] ?? formatEnum(value);

const normalizeTitleDisplay = (value: string) => value.replace(/\b(20\d{2})-\d{2}-\d{2}\b/g, '$1');

const friendlyTitle = (row: Row) => {
  const rawPrefix = row.titleType.trim();
  const rawName = row.name.toUpperCase();
  const prefix = rawName.startsWith(`TESOURO ${rawPrefix.toUpperCase()}`) ? `TESOURO ${rawPrefix}` : rawPrefix;
  if (rawName.startsWith(prefix.toUpperCase())) {
    return normalizeTitleDisplay(`Tesouro ${titleLabel(rawPrefix)}${row.name.slice(prefix.length)}`);
  }
  return normalizeTitleDisplay(row.name);
};

const titleTag = (value: string) => {
  const normalized = value.toUpperCase();
  if (normalized.includes('IPCA')) return 'IPCA+';
  if (normalized.includes('SELIC')) return 'Selic';
  if (normalized.includes('PREFIXADO')) return 'Prefixado';
  if (normalized.includes('IGPM')) return 'IGP-M+';
  if (normalized.includes('RENDA')) return 'Renda+';
  if (normalized.includes('EDUCA')) return 'Educa+';
  return titleLabel(value);
};

const instrumentCode = (row: Row) => {
  if (!row.idCem.toUpperCase().startsWith('TESOURO')) return row.idCem;
  const seriesDate = row.conversionDate ?? row.maturityDate;
  const year = seriesDate?.slice(2, 4) ?? row.name.match(/20(\d{2})/)?.[1] ?? '';
  const fullYear = seriesDate?.slice(0, 4) ?? '';
  const normalized = row.titleType.toUpperCase();
  if (normalized.includes('IPCA')) return `NTNB${year}`;
  if (normalized.includes('SELIC')) return `LFT${fullYear}`;
  if (normalized.includes('PREFIXADO')) return `LTN${fullYear}`;
  if (normalized.includes('IGPM')) return `NTNC${year}`;
  if (normalized.includes('RENDA')) return `RENDA+${fullYear}`;
  if (normalized.includes('EDUCA')) return `EDUCA+${fullYear}`;
  return row.idCem;
};

const titleTagSx = (value: string) => {
  const normalized = value.toUpperCase();
  if (normalized.includes('IPCA')) return { bgcolor: 'hsl(331 82% 94%)', color: 'hsl(331 60% 34%)' };
  if (normalized.includes('SELIC')) return { bgcolor: 'hsl(157 54% 91%)', color: 'hsl(158 54% 28%)' };
  if (normalized.includes('PREFIXADO')) return { bgcolor: 'hsl(211 88% 93%)', color: 'hsl(215 73% 34%)' };
  return { bgcolor: 'action.hover', color: 'text.primary' };
};

const liquidityFilterLabel = (value: string) => {
  if (value === 'TREASURY_DAILY_REPURCHASE') return 'D+0 / D+1 útil';
  if (value === 'D+0' || value === 'D0' || value === 'SAME_DAY') return 'D+0';
  if (value === 'D+1' || value === 'D1' || value === 'NEXT_BUSINESS_DAY') return 'D+1 útil';
  return formatEnum(value);
};

const liquidityLabel = (row: Row) => {
  if (row.liquidity !== 'TREASURY_DAILY_REPURCHASE') return liquidityFilterLabel(row.liquidity);
  return row.indexer === 'IPCA' || row.indexer === 'IGPM' ? 'D+1 útil' : 'D+0';
};

const LiquidityValue = ({ row }: { row: Row }) => {
  const value = liquidityLabel(row);
  const isSameDay = value === 'D+0';
  return (
    <Stack direction='row' alignItems='center' spacing={0.55}>
      <Box
        component='span'
        aria-hidden='true'
        sx={{
          width: 7,
          height: 7,
          flexShrink: 0,
          borderRadius: '50%',
          bgcolor: isSameDay ? 'hsl(190 70% 42%)' : 'hsl(258 70% 62%)',
        }}
      />
      <Typography
        component='span'
        variant='body2'
        noWrap
        sx={{
          fontWeight: 600,
          color: isSameDay ? 'hsl(190 64% 31%)' : 'hsl(258 48% 43%)',
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const formatRate = (row: Row) => {
  if (row.sellRateMorning === null) return null;
  const value = `${formatNumber(row.sellRateMorning, 3)}% a.a.`;
  if (row.indexer === 'IPCA') return `IPCA + ${value}`;
  if (row.indexer === 'SELIC') return `Selic + ${value}`;
  return value;
};

function MissingValue({ reason = 'Não calculável com os dados disponíveis para este título.' }: { reason?: string }) {
  return (
    <Tooltip title={reason} arrow>
      <Box component='span' sx={{ whiteSpace: 'nowrap', cursor: 'help' }}>
        —{' '}
        <Typography component='span' variant='caption' color='text.secondary'>
          ⓘ
        </Typography>
      </Box>
    </Tooltip>
  );
}

const metric = (value: number | null, suffix = '', digits = 2): ReactNode =>
  value === null || value === 0 ? <MissingValue /> : `${formatNumber(value, digits)}${suffix}`;

const latestDate = (rows: Row[], field: 'quoteDate' | 'sourceAsOf') =>
  rows
    .map((row) => row[field])
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1) ?? null;

export default function FixedIncomeCatalog() {
  const [response, setResponse] = useState<Response | null>(null);
  const [stableOptions, setStableOptions] = useState<Response['options'] | null>(null);
  const [error, setError] = useState(false);
  const [q, setQ] = useState('');
  const [titleType, setTitleType] = useState('');
  const [indexer, setIndexer] = useState('');
  const [cashFlowType, setCashFlowType] = useState('');
  const [remunerationType, setRemunerationType] = useState('');
  const [riskFactor, setRiskFactor] = useState('');
  const [taxRegime, setTaxRegime] = useState('');
  const [liquidity, setLiquidity] = useState('');
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [minPu, setMinPu] = useState('');
  const [maxPu, setMaxPu] = useState('');
  const [minDays, setMinDays] = useState('');
  const [maxDays, setMaxDays] = useState('');
  const [minDuration, setMinDuration] = useState('');
  const [maxDuration, setMaxDuration] = useState('');
  const [minModifiedDuration, setMinModifiedDuration] = useState('');
  const [maxModifiedDuration, setMaxModifiedDuration] = useState('');
  const [minDv01, setMinDv01] = useState('');
  const [maxDv01, setMaxDv01] = useState('');
  const [minConvexity, setMinConvexity] = useState('');
  const [maxConvexity, setMaxConvexity] = useState('');
  const [maturityFrom, setMaturityFrom] = useState(defaultMaturityFrom);
  const [maturityTo, setMaturityTo] = useState('');
  const [sort, setSort] = useState<'maturity' | 'rate'>('rate');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [viewModeHydrated, setViewModeHydrated] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(['modifiedDurationYears', 'dv01', 'convexity']);
  const [selectedChartRow, setSelectedChartRow] = useState<Row | null>(null);
  const [hoveredChartRow, setHoveredChartRow] = useState<{ row: Row; x: number; y: number } | null>(null);
  const [appliedQuery, setAppliedQuery] = useState(`maturityFrom=${defaultMaturityFrom}`);
  const [appliedFilterLabels, setAppliedFilterLabels] = useState<string[]>([
    `Vencimento desde: ${formatDate(defaultMaturityFrom)}`,
  ]);
  const isMobile = useMediaQuery('(max-width:899px)');
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  useEffect(() => {
    const savedView = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith('fixed-income-view='))
      ?.split('=')[1];
    if (savedView === 'table' || savedView === 'cards' || savedView === 'charts') setViewMode(savedView);
    setViewModeHydrated(true);
  }, []);

  useEffect(() => {
    if (viewModeHydrated) {
      document.cookie = `fixed-income-view=${viewMode}; path=/; max-age=31536000; samesite=lax`;
    }
  }, [viewMode, viewModeHydrated]);

  useEffect(() => {
    const controller = new AbortController();
    setError(false);
    fetch(`/api/renda-fixa/catalogo?${appliedQuery}`, { signal: controller.signal, cache: 'no-store' })
      .then((result) => (result.ok ? (result.json() as Promise<Response>) : Promise.reject(new Error('catalog'))))
      .then((payload) => {
        setResponse(payload);
        setStableOptions((current) => current ?? payload.options);
      })
      .catch((reason: unknown) => {
        if ((reason as Error).name !== 'AbortError') setError(true);
      });
    return () => controller.abort();
  }, [appliedQuery]);

  const rows = useMemo(
    () =>
      [...(response?.data ?? [])].sort((a, b) =>
        sort === 'rate'
          ? (b.sellRateMorning ?? -Infinity) - (a.sellRateMorning ?? -Infinity)
          : a.daysToMaturity - b.daysToMaturity,
      ),
    [response, sort],
  );
  const availableOptions = stableOptions ?? response?.options;
  const compareHref = `/renda-fixa/comparar?ids=${selected.join(',')}`;
  const toggleSelected = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : current.length < 4 ? [...current, id] : current,
    );

  const filterValues = useMemo(
    () => ({
      q,
      titleType,
      indexer,
      cashFlowType,
      remunerationType,
      riskFactor,
      taxRegime,
      liquidity,
      minRate,
      maxRate,
      minPu,
      maxPu,
      minDays,
      maxDays,
      minDuration,
      maxDuration,
      minModifiedDuration,
      maxModifiedDuration,
      minDv01,
      maxDv01,
      minConvexity,
      maxConvexity,
      maturityFrom,
      maturityTo,
    }),
    [
      q,
      titleType,
      indexer,
      cashFlowType,
      remunerationType,
      riskFactor,
      taxRegime,
      liquidity,
      minRate,
      maxRate,
      minPu,
      maxPu,
      minDays,
      maxDays,
      minDuration,
      maxDuration,
      minModifiedDuration,
      maxModifiedDuration,
      minDv01,
      maxDv01,
      minConvexity,
      maxConvexity,
      maturityFrom,
      maturityTo,
    ],
  );

  const filterQuery = useMemo(
    () => new URLSearchParams(Object.fromEntries(Object.entries(filterValues).filter(([, value]) => value))).toString(),
    [filterValues],
  );

  const draftFilterLabels = useMemo(
    () =>
      [
        q && `Busca: ${q}`,
        titleType && `Tipo: ${titleLabel(titleType)}`,
        indexer && `Indexador: ${formatEnum(indexer)}`,
        remunerationType && `Remuneração: ${formatEnum(remunerationType)}`,
        cashFlowType && `Fluxo: ${formatEnum(cashFlowType)}`,
        maturityFrom && `Vencimento desde: ${formatDate(maturityFrom)}`,
        maturityTo && `Vencimento até: ${formatDate(maturityTo)}`,
        minRate && `Taxa mínima: ${minRate}%`,
        maxRate && `Taxa máxima: ${maxRate}%`,
        minPu && `PU mínimo: ${formatPrice(Number(minPu))}`,
        maxPu && `PU máximo: ${formatPrice(Number(maxPu))}`,
        minDays && `Prazo mínimo: ${minDays} dias`,
        maxDays && `Prazo máximo: ${maxDays} dias`,
        minDuration && `Duração mínima: ${minDuration} anos`,
        maxDuration && `Duração máxima: ${maxDuration} anos`,
        minModifiedDuration && `Duração modificada mínima: ${minModifiedDuration}`,
        maxModifiedDuration && `Duração modificada máxima: ${maxModifiedDuration}`,
        minDv01 && `DV01 mínimo: ${minDv01}`,
        maxDv01 && `DV01 máximo: ${maxDv01}`,
        minConvexity && `Convexidade mínima: ${minConvexity}`,
        maxConvexity && `Convexidade máxima: ${maxConvexity}`,
        liquidity && `Liquidez: ${liquidityFilterLabel(liquidity)}`,
        taxRegime && `Tributação: ${formatEnum(taxRegime)}`,
        riskFactor && `Fator de risco: ${formatEnum(riskFactor)}`,
      ].filter(Boolean) as string[],
    [
      q,
      titleType,
      indexer,
      remunerationType,
      cashFlowType,
      maturityFrom,
      maturityTo,
      minRate,
      maxRate,
      minPu,
      maxPu,
      minDays,
      maxDays,
      minDuration,
      maxDuration,
      minModifiedDuration,
      maxModifiedDuration,
      minDv01,
      maxDv01,
      minConvexity,
      maxConvexity,
      liquidity,
      taxRegime,
      riskFactor,
    ],
  );

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setAppliedQuery(filterQuery);
      setAppliedFilterLabels(draftFilterLabels);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [draftFilterLabels, filterQuery]);

  const clearFilters = () => {
    setQ('');
    setTitleType('');
    setIndexer('');
    setCashFlowType('');
    setRemunerationType('');
    setRiskFactor('');
    setTaxRegime('');
    setLiquidity('');
    setMinRate('');
    setMaxRate('');
    setMinPu('');
    setMaxPu('');
    setMinDays('');
    setMaxDays('');
    setMinDuration('');
    setMaxDuration('');
    setMinModifiedDuration('');
    setMaxModifiedDuration('');
    setMinDv01('');
    setMaxDv01('');
    setMinConvexity('');
    setMaxConvexity('');
    setMaturityFrom('');
    setMaturityTo('');
    setAppliedQuery('');
    setAppliedFilterLabels([]);
    setMobileFiltersOpen(false);
  };

  const select = (
    label: string,
    value: string,
    setValue: (value: string) => void,
    options: string[],
    format: (value: string) => string = (value) => formatEnum(value),
  ) => (
    <FormControl size='small' fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select value={value} label={label} onChange={(event) => setValue(event.target.value)}>
        <MenuItem value=''>Todos</MenuItem>
        {options.map((item) => (
          <MenuItem key={item} value={item}>
            {format(item)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  const range = (
    leftLabel: string,
    left: string,
    setLeft: (value: string) => void,
    rightLabel: string,
    right: string,
    setRight: (value: string) => void,
    type = 'number',
  ) => (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
      <TextField
        size='small'
        label={leftLabel}
        type={type}
        value={left}
        onChange={(event) => setLeft(event.target.value)}
        inputProps={type === 'date' ? { lang: 'pt-BR' } : undefined}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      />
      <TextField
        size='small'
        label={rightLabel}
        type={type}
        value={right}
        onChange={(event) => setRight(event.target.value)}
        inputProps={type === 'date' ? { lang: 'pt-BR' } : undefined}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      />
    </Box>
  );

  const filterGroup = (label: string, content: ReactNode, advanced = false) => (
    <Box sx={{ mt: advanced ? 1.75 : 0 }}>
      <Typography variant='overline' color='text.secondary' sx={{ fontSize: 10, fontWeight: 800 }}>
        {label}
      </Typography>
      <Stack spacing={1.15} sx={{ mt: 0.5 }}>
        {content}
      </Stack>
    </Box>
  );

  const filterPanel = (
    <Stack spacing={1.5}>
      {filterGroup(
        'BÁSICOS',
        <>
          <TextField
            size='small'
            label='Buscar título ou código'
            value={q}
            onChange={(event) => setQ(event.target.value)}
            InputProps={{ startAdornment: <Search size={16} style={{ marginRight: 6 }} /> }}
          />
          {select('Tipo de título', titleType, setTitleType, availableOptions?.titleTypes ?? [], titleLabel)}
          {select('Indexador', indexer, setIndexer, availableOptions?.indexers ?? [])}
          {select('Remuneração', remunerationType, setRemunerationType, availableOptions?.remunerationTypes ?? [])}
          {select('Fluxo', cashFlowType, setCashFlowType, availableOptions?.cashFlowTypes ?? [])}
        </>,
      )}
      {filterGroup(
        'PRAZO',
        <>
          <Typography variant='caption' color='text.secondary'>
            Vencimento
          </Typography>
          {range('Data inicial', maturityFrom, setMaturityFrom, 'Data final', maturityTo, setMaturityTo, 'date')}
          <Typography variant='caption' color='text.secondary'>
            Prazo restante (dias)
          </Typography>
          {range('Mínimo', minDays, setMinDays, 'Máximo', maxDays, setMaxDays)}
        </>,
        true,
      )}
      {filterGroup(
        'TAXAS E PREÇOS',
        <>
          <Typography variant='caption' color='text.secondary'>
            Taxa de remuneração (% a.a.)
          </Typography>
          {range('Mínima', minRate, setMinRate, 'Máxima', maxRate, setMaxRate)}
          <Typography variant='caption' color='text.secondary'>
            PU (preço unitário)
          </Typography>
          {range('Mínimo', minPu, setMinPu, 'Máximo', maxPu, setMaxPu)}
        </>,
        true,
      )}
      <Button
        variant='outlined'
        size='small'
        fullWidth
        startIcon={<SlidersHorizontal size={15} />}
        onClick={() => setShowAdvanced((value) => !value)}
      >
        {showAdvanced ? 'Menos filtros' : 'Mais filtros'}
      </Button>
      <Collapse in={showAdvanced}>
        {filterGroup(
          'RISCO E SENSIBILIDADE',
          <>
            {select('Liquidez', liquidity, setLiquidity, availableOptions?.liquidities ?? [], liquidityFilterLabel)}
            {select('Tributação', taxRegime, setTaxRegime, availableOptions?.taxRegimes ?? [])}
            {select('Fator de risco', riskFactor, setRiskFactor, availableOptions?.riskFactors ?? [])}
            <Typography variant='caption' color='text.secondary'>
              Duração (anos)
            </Typography>
            {range('Mínima', minDuration, setMinDuration, 'Máxima', maxDuration, setMaxDuration)}
            <Typography variant='caption' color='text.secondary'>
              Duração modificada (anos)
            </Typography>
            {range(
              'Mínima',
              minModifiedDuration,
              setMinModifiedDuration,
              'Máxima',
              maxModifiedDuration,
              setMaxModifiedDuration,
            )}
            <Typography variant='caption' color='text.secondary'>
              DV01 (R$ por 1 bp)
            </Typography>
            {range('Mínimo', minDv01, setMinDv01, 'Máximo', maxDv01, setMaxDv01)}
            <Typography variant='caption' color='text.secondary'>
              Convexidade
            </Typography>
            {range('Mínima', minConvexity, setMinConvexity, 'Máxima', maxConvexity, setMaxConvexity)}
          </>,
          true,
        )}
      </Collapse>
      <Typography variant='caption' color='text.secondary' textAlign='center'>
        {rows.length} resultados encontrados
      </Typography>
    </Stack>
  );

  const cell = (content: ReactNode) => (
    <Box sx={{ lineHeight: 1.25, minWidth: 0, overflow: 'hidden', '& > span': { display: 'block' } }}>{content}</Box>
  );

  const gridColumns = isMobile
    ? [
        {
          headerText: '',
          width: '9%',
          template: (row: Row) => (
            <Checkbox
              size='small'
              checked={selected.includes(row.id)}
              onChange={() => toggleSelected(row.id)}
              inputProps={{ 'aria-label': `Selecionar ${friendlyTitle(row)}` }}
            />
          ),
        },
        {
          field: 'name',
          headerText: 'Título',
          width: '43%',
          template: (row: Row) => (
            <Box sx={{ minWidth: 0 }}>
              <AppLink
                href={`/ativo/${encodeURIComponent(row.idCem)}`}
                underline='hover'
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {friendlyTitle(row)}
              </AppLink>
              <Typography variant='caption' color='text.secondary' noWrap>
                {instrumentCode(row)}
              </Typography>
            </Box>
          ),
        },
        {
          field: 'sellRateMorning',
          headerText: 'Taxa',
          width: '22%',
          template: (row: Row) => formatRate(row) ?? <MissingValue />,
        },
        {
          field: 'maturityDate',
          headerText: 'Vencimento',
          width: '26%',
          template: (row: Row) =>
            cell(
              <>
                <span>{formatDate(row.maturityDate)}</span>
                {row.conversionDate ? (
                  <Typography variant='caption' display='block' color='text.secondary'>
                    Início: {formatDate(row.conversionDate)}
                  </Typography>
                ) : null}
                <Typography variant='caption' display='block' color='text.secondary'>
                  {formatNumber(row.daysToMaturity, 0)} dias
                </Typography>
              </>,
            ),
        },
      ]
    : [
        {
          headerText: '',
          width: '4%',
          template: (row: Row) => (
            <Checkbox
              size='small'
              checked={selected.includes(row.id)}
              onChange={() => toggleSelected(row.id)}
              inputProps={{ 'aria-label': `Selecionar ${friendlyTitle(row)}` }}
            />
          ),
        },
        {
          field: 'name',
          headerText: 'Título / código',
          width: '18%',
          template: (row: Row) => (
            <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
              <AppLink
                href={`/ativo/${encodeURIComponent(row.idCem)}`}
                underline='hover'
                sx={{
                  display: 'block',
                  fontWeight: 700,
                  lineHeight: 1.25,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {friendlyTitle(row)}
              </AppLink>
              <Typography variant='caption' display='block' color='text.secondary' noWrap>
                {instrumentCode(row)}
              </Typography>
            </Box>
          ),
        },
        {
          field: 'indexer',
          headerText: 'Tipo / indexador',
          width: '11%',
          template: (row: Row) =>
            cell(
              <>
                <span>{titleTag(row.titleType)}</span>
                <Typography variant='caption' display='block' color='text.secondary'>
                  {formatEnum(row.indexer)}
                </Typography>
              </>,
            ),
        },
        {
          field: 'sellRateMorning',
          headerText: 'Remuneração',
          width: '12%',
          template: (row: Row) =>
            cell(
              <>
                <span>{formatRate(row) ?? <MissingValue />}</span>
                <Typography variant='caption' display='block' color='text.secondary'>
                  {formatEnum(row.remunerationType)}
                </Typography>
              </>,
            ),
        },
        {
          field: 'sellPuMorning',
          headerText: 'PU / referência',
          width: '11%',
          template: (row: Row) =>
            cell(
              <>
                <span>{row.sellPuMorning === null ? <MissingValue /> : formatPrice(row.sellPuMorning)}</span>
                <Typography variant='caption' display='block' color='text.secondary'>
                  {formatDate(row.quoteDate)}
                </Typography>
              </>,
            ),
        },
        {
          field: 'maturityDate',
          headerText: 'Vencimento',
          width: '10%',
          template: (row: Row) =>
            cell(
              <>
                <span>{formatDate(row.maturityDate)}</span>
                <Typography variant='caption' display='block' color='text.secondary'>
                  {formatNumber(row.daysToMaturity, 0)} dias
                </Typography>
              </>,
            ),
        },
        {
          field: 'durationYears',
          headerText: 'Duração',
          width: '7%',
          template: (row: Row) => metric(row.durationYears, ' anos'),
        },
        {
          field: 'modifiedDurationYears',
          headerText: 'Duração mod.',
          width: '8%',
          visible: !hiddenColumns.includes('modifiedDurationYears'),
          template: (row: Row) => metric(row.modifiedDurationYears, ' anos'),
        },
        {
          field: 'dv01',
          headerText: 'DV01',
          width: '7%',
          visible: !hiddenColumns.includes('dv01'),
          template: (row: Row) => (row.dv01 === null || row.dv01 === 0 ? <MissingValue /> : formatPrice(row.dv01)),
        },
        {
          field: 'convexity',
          headerText: 'Convexidade',
          width: '8%',
          visible: !hiddenColumns.includes('convexity'),
          template: (row: Row) => metric(row.convexity),
        },
        {
          field: 'liquidity',
          headerText: 'Liquidez',
          width: '7%',
          template: (row: Row) => <LiquidityValue row={row} />,
        },
        {
          field: 'taxRegime',
          headerText: 'Tributação',
          width: '7%',
          template: (row: Row) => formatEnum(row.taxRegime),
        },
      ];

  const columnOptions = [
    ['durationYears', 'Duração'],
    ['modifiedDurationYears', 'Duração modificada'],
    ['dv01', 'DV01'],
    ['convexity', 'Convexidade'],
    ['liquidity', 'Liquidez'],
    ['taxRegime', 'Tributação'],
  ] as const;

  const chartRows = rows.filter((row) => row.sellRateMorning !== null);
  const typeColor = (type: string) => {
    const normalized = type.toUpperCase();
    if (normalized.includes('IPCA')) return '#7c3aed';
    if (normalized.includes('SELIC')) return '#0f766e';
    if (normalized.includes('PREFIXADO')) return '#2563eb';
    return '#64748b';
  };

  const renderCharts = () => {
    const points = chartRows;
    const chartWidth = 1000;
    const chartHeight = 500;
    const plotLeft = 94;
    const plotRight = 944;
    const plotTop = 42;
    const plotBottom = 410;
    const maxYears = Math.max(...points.map((row) => row.daysToMaturity / 365.25), 1);
    const xTickStep = maxYears <= 1 ? 0.25 : maxYears <= 3 ? 0.5 : maxYears <= 10 ? 1 : maxYears <= 20 ? 5 : 10;
    const xAxisMax = Math.ceil(maxYears / xTickStep) * xTickStep;
    const xTicks = Array.from({ length: Math.round(xAxisMax / xTickStep) + 1 }, (_, index) =>
      Number((index * xTickStep).toFixed(2)),
    );
    const maxRate = Math.max(...points.map((row) => row.sellRateMorning ?? 0), 1);
    const roughRateStep = maxRate / 4;
    const rateStepPrecision = roughRateStep < 1 ? 0.1 : roughRateStep < 5 ? 0.5 : 1;
    const rateTickStep = Math.max(rateStepPrecision, Math.ceil(roughRateStep / rateStepPrecision) * rateStepPrecision);
    const rateAxisMax = rateTickStep * 4;
    const rateTicks = Array.from({ length: 5 }, (_, index) => Number((index * rateTickStep).toFixed(2))).reverse();
    const xFor = (years: number) => plotLeft + (years / xAxisMax) * (plotRight - plotLeft);
    const yFor = (rate: number) => plotBottom - (rate / rateAxisMax) * (plotBottom - plotTop);
    const formatChartTerm = (years: number) => {
      const months = Math.round(years * 12);
      if (months < 12) return `${months} ${months === 1 ? 'mês' : 'meses'}`;
      const displayedYears = months / 12;
      return `${displayedYears.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${displayedYears === 1 ? 'ano' : 'anos'}`;
    };
    const formatRateTick = (rate: number) =>
      `${rate.toLocaleString('pt-BR', { maximumFractionDigits: rate < 1 ? 2 : 1 })}%`;
    const benchmarkItems = [
      { key: 'selic', label: 'Selic atual', unit: '% a.a.', point: response?.benchmarks?.selic },
      { key: 'ipca', label: 'IPCA atual', unit: '% no mês', point: response?.benchmarks?.ipca },
      { key: 'igpm', label: 'IGP-M atual', unit: '% no mês', point: response?.benchmarks?.igpm },
    ];
    return (
      <Paper
        variant='outlined'
        sx={{
          p: { xs: 1, md: 2 },
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent='space-between' gap={1} sx={{ mb: 1 }}>
          <Box>
            <Typography fontWeight={800}>Taxa × vencimento</Typography>
            <Typography variant='caption' color='text.secondary'>
              Cada ponto representa um título do universo filtrado. As taxas têm bases diferentes entre indexadores.
            </Typography>
          </Box>
        </Stack>
        <Stack spacing={0.75} sx={{ mb: 1.25 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1}>
            <Typography variant='caption' fontWeight={800}>
              Referências atuais do mercado
            </Typography>
            <AppLink
              href='/mercado'
              underline='hover'
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.35,
                fontSize: '0.75rem',
                whiteSpace: 'nowrap',
              }}
            >
              Ver detalhes no Mercado <ArrowUpRight size={13} />
            </AppLink>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75}>
            {benchmarkItems.map((item) => (
              <AppLink
                key={item.key}
                href='/mercado'
                underline='none'
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                  px: 1,
                  py: 0.7,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.25,
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '&:hover': { borderColor: 'primary.main', bgcolor: 'action.selected' },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant='caption' color='text.secondary' noWrap display='block'>
                    {item.label}
                  </Typography>
                  <Typography variant='body2' fontWeight={800} noWrap>
                    {item.point ? `${formatNumber(item.point.value, 2)}${item.unit}` : 'Sem dado disponível'}
                  </Typography>
                </Box>
                <Typography variant='caption' color='text.secondary' noWrap>
                  {item.point?.date ? formatDate(item.point.date) : 'Ver no Mercado'}
                </Typography>
              </AppLink>
            ))}
          </Stack>
        </Stack>
        <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%', minWidth: 0, minHeight: 0, flex: 1 }}>
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              width='100%'
              height='100%'
              preserveAspectRatio='none'
              role='img'
              aria-label='Gráfico de taxa por vencimento'
              aria-describedby='fixed-income-chart-description'
            >
              <title>Taxa por vencimento dos títulos filtrados</title>
              <desc id='fixed-income-chart-description'>
                O eixo vertical mostra a taxa indicada em percentual ao ano. O eixo horizontal mostra o prazo restante
                até o vencimento em anos ou meses. Cada ponto é um título clicável.
              </desc>
              {rateTicks.map((rate) => {
                const y = yFor(rate);
                return (
                  <g key={`rate-tick-${rate}`}>
                    <line
                      x1={plotLeft}
                      y1={y}
                      x2={plotRight}
                      y2={y}
                      stroke='hsl(var(--border))'
                      strokeDasharray='4 4'
                    />
                    <text
                      x={plotLeft - 10}
                      y={y + 4}
                      textAnchor='end'
                      fill='hsl(var(--muted-foreground))'
                      fontSize='12'
                    >
                      {formatRateTick(rate)}
                    </text>
                  </g>
                );
              })}
              {xTicks.map((years) => {
                const x = xFor(years);
                return (
                  <g key={`term-tick-${years}`}>
                    <line
                      x1={x}
                      y1={plotTop}
                      x2={x}
                      y2={plotBottom}
                      stroke='hsl(var(--border))'
                      strokeDasharray='2 6'
                      opacity='0.65'
                    />
                    <text
                      x={x}
                      y={plotBottom + 24}
                      textAnchor='middle'
                      fill='hsl(var(--muted-foreground))'
                      fontSize='12'
                    >
                      {formatChartTerm(years)}
                    </text>
                  </g>
                );
              })}
              <line x1={plotLeft} y1={plotTop} x2={plotLeft} y2={plotBottom} stroke='hsl(var(--foreground) / 0.32)' />
              <line
                x1={plotLeft}
                y1={plotBottom}
                x2={plotRight}
                y2={plotBottom}
                stroke='hsl(var(--foreground) / 0.32)'
              />
              <text
                x='22'
                y={(plotTop + plotBottom) / 2}
                textAnchor='middle'
                transform={`rotate(-90 22 ${(plotTop + plotBottom) / 2})`}
                fill='hsl(var(--muted-foreground))'
                fontSize='12'
                fontWeight='700'
              >
                Taxa indicada (% a.a.)
              </text>
              <text
                x={(plotLeft + plotRight) / 2}
                y={chartHeight - 10}
                textAnchor='middle'
                fill='hsl(var(--muted-foreground))'
                fontSize='12'
                fontWeight='700'
              >
                Prazo restante até o vencimento
              </text>
              {points.map((row) => {
                const x = xFor(row.daysToMaturity / 365.25);
                const y = yFor(row.sellRateMorning ?? 0);
                const pointLabel = `Abrir detalhes de ${friendlyTitle(row)}`;
                return (
                  <circle
                    key={row.id}
                    cx={x}
                    cy={y}
                    r='8'
                    fill={typeColor(row.titleType)}
                    stroke='hsl(var(--card))'
                    strokeWidth='2'
                    opacity='0.9'
                    role='button'
                    tabIndex={0}
                    aria-label={pointLabel}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setSelectedChartRow(row)}
                    onMouseEnter={() => setHoveredChartRow({ row, x, y })}
                    onMouseLeave={() => setHoveredChartRow(null)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setSelectedChartRow(row);
                      }
                    }}
                  ></circle>
                );
              })}
            </svg>
            {hoveredChartRow && (
              <Paper
                elevation={4}
                sx={{
                  position: 'absolute',
                  left: `${Math.min(Math.max((hoveredChartRow.x / chartWidth) * 100, 22), 78)}%`,
                  top: `${(hoveredChartRow.y / chartHeight) * 100}%`,
                  transform: 'translate(-50%, -105%)',
                  p: 1,
                  minWidth: 190,
                  maxWidth: 'min(250px, calc(100% - 16px))',
                  pointerEvents: 'none',
                  zIndex: 1,
                }}
              >
                <Typography variant='caption' display='block' fontWeight={800} noWrap>
                  {friendlyTitle(hoveredChartRow.row)}
                </Typography>
                <Typography variant='caption' display='block' color='text.secondary'>
                  {instrumentCode(hoveredChartRow.row)}
                </Typography>
                <Stack direction='row' spacing={1.25} sx={{ mt: 0.5 }}>
                  <Typography variant='caption'>Taxa: {formatRate(hoveredChartRow.row) ?? '—'}</Typography>
                  <Typography variant='caption'>Venc.: {formatDate(hoveredChartRow.row.maturityDate)}</Typography>
                </Stack>
                <Typography variant='caption' display='block' color='text.secondary'>
                  Duração: {metric(hoveredChartRow.row.durationYears, ' anos')}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
        <Stack direction='row' spacing={2} flexWrap='wrap' useFlexGap sx={{ mt: 0.5 }}>
          {Array.from(new Set(chartRows.map((row) => titleTag(row.titleType)))).map((tag) => (
            <Stack key={tag} direction='row' alignItems='center' spacing={0.5}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: typeColor(tag) }} />
              <Typography variant='caption'>{tag}</Typography>
            </Stack>
          ))}
        </Stack>
        <Stack
          direction='row'
          alignItems='flex-start'
          spacing={1}
          role='note'
          sx={{
            mt: 1.25,
            p: 1,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'action.hover',
          }}
        >
          <MousePointerClick size={17} aria-hidden='true' />
          <Box>
            <Typography variant='caption' fontWeight={800} display='block'>
              Como ler este gráfico
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Compare a taxa indicada no eixo vertical com o prazo restante no eixo horizontal. Clique em qualquer ponto
              para abrir os detalhes do título; os indexadores têm bases de remuneração diferentes.
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  const renderCard = (row: Row) => (
    <Paper
      key={row.id}
      variant='outlined'
      className='fixed-income-card'
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        color: isDark ? 'hsl(135 25% 14%)' : 'text.primary',
        borderColor: isDark ? 'hsl(210 16% 78%)' : 'divider',
        '& .MuiTypography-colorTextSecondary': {
          color: isDark ? 'hsl(130 12% 34%)' : 'text.secondary',
        },
        '& .MuiButton-outlined': {
          color: isDark ? 'hsl(135 25% 14%)' : 'inherit',
          borderColor: isDark ? 'hsl(135 18% 48%)' : 'inherit',
        },
      }}
    >
      <Stack direction='row' alignItems='center' spacing={0.75} sx={{ minWidth: 0 }}>
        <Chip
          size='small'
          label={titleTag(row.titleType)}
          sx={{ height: 22, fontWeight: 700, ...titleTagSx(row.titleType) }}
        />
        <Typography variant='caption' color='text.secondary' noWrap>
          {instrumentCode(row)}
        </Typography>
      </Stack>
      <Stack direction='row' justifyContent='space-between' alignItems='flex-start' gap={1} sx={{ mt: 0.75 }}>
        <Box sx={{ minWidth: 0 }}>
          <AppLink
            href={`/ativo/${encodeURIComponent(row.idCem)}`}
            sx={{
              fontWeight: 800,
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              lineHeight: 1.2,
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
              overflow: 'hidden',
              minHeight: '2.4em',
              color: 'inherit',
              textDecoration: 'none',
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            {friendlyTitle(row)}
          </AppLink>
          <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.2, mt: 0.35 }}>
            {formatRate(row) ?? <MissingValue />}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            PU de referência
          </Typography>
          <Typography variant='body2' fontWeight={800}>
            {row.sellPuMorning === null ? <MissingValue /> : formatPrice(row.sellPuMorning)}
          </Typography>
          <Typography variant='caption' color='text.secondary' display='block'>
            em {formatDate(row.quoteDate)}
          </Typography>
        </Box>
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
          mt: 1.1,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ py: 0.75, pr: 0.75 }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            Vencimento
          </Typography>
          <Typography variant='body2' fontWeight={700} noWrap>
            {formatDate(row.maturityDate)}
          </Typography>
          {row.conversionDate ? (
            <Typography variant='caption' color='text.secondary' display='block' noWrap>
              Início: {formatDate(row.conversionDate)}
            </Typography>
          ) : null}
          <Typography variant='caption' color='text.secondary' display='block'>
            {formatNumber(row.daysToMaturity / 365, 1)} anos
          </Typography>
        </Box>
        <Box sx={{ py: 0.75, px: 0.75, borderLeft: 1, borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            Duração
          </Typography>
          <Typography variant='body2' noWrap>
            {metric(row.durationYears, ' anos')}
          </Typography>
        </Box>
        <Box sx={{ py: 0.75, px: 0.75, borderLeft: 1, borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            DV01
          </Typography>
          <Typography variant='body2' noWrap>
            {row.dv01 === null || row.dv01 === 0 ? <MissingValue /> : formatPrice(row.dv01)}
          </Typography>
        </Box>
        <Box sx={{ py: 0.75, pl: 0.75, borderLeft: 1, borderColor: 'divider' }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            Convexidade
          </Typography>
          <Typography variant='body2' noWrap>
            {metric(row.convexity)}
          </Typography>
        </Box>
      </Box>
      <Stack direction='row' gap={1.5} sx={{ mt: 0.9 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            Liquidez
          </Typography>
          <LiquidityValue row={row} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant='caption' color='text.secondary' display='block'>
            Tributação
          </Typography>
          <Typography variant='body2' noWrap>
            {formatEnum(row.taxRegime)}
          </Typography>
        </Box>
      </Stack>
      <Stack direction='row' alignItems='center' justifyContent='space-between' gap={1} sx={{ mt: 0.5 }}>
        <Stack direction='row' alignItems='center' sx={{ minWidth: 0 }}>
          <Checkbox
            size='small'
            checked={selected.includes(row.id)}
            onChange={() => toggleSelected(row.id)}
            inputProps={{ 'aria-label': `Selecionar ${friendlyTitle(row)} para comparar` }}
            sx={{ p: 0.25, mr: 0.25 }}
          />
          <Typography variant='caption'>Comparar</Typography>
        </Stack>
        <Button
          component={AppLink}
          href={`/ativo/${encodeURIComponent(row.idCem)}`}
          size='small'
          variant='outlined'
          sx={{ minHeight: 29, px: 1.1, whiteSpace: 'nowrap' }}
        >
          Ver detalhes&nbsp; →
        </Button>
      </Stack>
    </Paper>
  );

  if (!response && !error) {
    return (
      <Stack alignItems='center' sx={{ py: 8 }}>
        <CircularProgress aria-label='Carregando catálogo' />
      </Stack>
    );
  }

  const quoteDate = latestDate(rows, 'quoteDate');
  const sourceAsOf = latestDate(rows, 'sourceAsOf');

  return (
    <Box
      sx={{
        px: { xs: 1.5, md: 3 },
        py: { xs: 1.5, md: 1.75 },
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        '& .MuiButton-root': { textTransform: 'none' },
      }}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        justifyContent='space-between'
        alignItems={{ md: 'stretch' }}
        gap={1.5}
        sx={{ mb: 2 }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            component='h1'
            sx={{ fontSize: { xs: '1.8rem', md: '2.1rem' }, lineHeight: 1.1, fontWeight: 800, letterSpacing: '-.04em' }}
          >
            Renda Fixa – Tesouro Direto{' '}
            <Tooltip
              title='Compare características e condições publicadas, sem transformar os dados em recomendação.'
              arrow
            >
              <Typography component='span' color='text.secondary' sx={{ fontSize: '1rem', cursor: 'help' }}>
                ⓘ
              </Typography>
            </Tooltip>
          </Typography>
          <Typography color='text.secondary' variant='body2' sx={{ mt: 0.5 }}>
            Encontre, compare e entenda os títulos do Tesouro Direto.
          </Typography>
          {isMobile && (
            <Button
              variant='outlined'
              startIcon={<SlidersHorizontal size={16} />}
              onClick={() => setMobileFiltersOpen(true)}
              fullWidth
              sx={{ mt: 1.25 }}
            >
              Filtros{appliedFilterLabels.length > 0 ? ` (${appliedFilterLabels.length})` : ''}
            </Button>
          )}
        </Box>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ md: 'stretch' }}
          gap={1}
          sx={{ flex: { md: '0 1 860px' }, minWidth: 0 }}
        >
          <Paper
            variant='outlined'
            sx={{
              p: { xs: 1.25, md: 1.5 },
              flex: 1,
              minWidth: 0,
              borderRadius: 1,
              bgcolor: isDark ? 'hsl(160 28% 9%)' : 'hsl(var(--card))',
              borderColor: 'hsl(var(--border))',
              color: 'hsl(var(--foreground))',
              boxShadow: isDark ? '0 8px 20px hsl(160 28% 9% / 0.14)' : '0 8px 20px hsl(var(--foreground) / 0.08)',
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems='center'
              spacing={{ xs: 1.25, sm: 1.5 }}
              divider={<Divider orientation='vertical' flexItem sx={{ borderColor: 'hsl(var(--border))' }} />}
              sx={{ height: '100%' }}
            >
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ minWidth: 0, flex: '1 1 0', overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    border: '1px solid hsl(157 54% 38%)',
                    borderRadius: '50%',
                    color: isDark ? 'hsl(157 54% 58%)' : 'hsl(var(--primary))',
                  }}
                >
                  <ShieldCheck size={17} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <Typography variant='body2' fontWeight={800} noWrap>
                    Dados oficiais
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    noWrap
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={`${rows.length} títulos · Fonte: ${formatEnum(rows[0]?.source ?? 'tesouro_transparente')}`}
                  >
                    {rows.length} títulos · Fonte: {formatEnum(rows[0]?.source ?? 'tesouro_transparente')}
                  </Typography>
                </Box>
              </Stack>
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                sx={{ minWidth: 0, flex: '1 1 0', overflow: 'hidden' }}
              >
                <Box
                  sx={{
                    width: 34,
                    height: 34,
                    display: 'grid',
                    placeItems: 'center',
                    flexShrink: 0,
                    border: '1px solid hsl(157 54% 38%)',
                    borderRadius: '50%',
                    color: isDark ? 'hsl(157 54% 58%)' : 'hsl(var(--primary))',
                  }}
                >
                  <Clock3 size={17} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <Typography variant='body2' fontWeight={800} noWrap>
                    {sourceAsOf === new Date().toISOString().slice(0, 10) ? 'Atualizado hoje' : 'Atualizado em'}
                  </Typography>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    noWrap
                    sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}
                    title={formatDate(sourceAsOf)}
                  >
                    {formatDate(sourceAsOf)}
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Paper>
          <Button
            component={AppLink}
            href='/referencia-rapida#renda-fixa'
            variant='outlined'
            size='small'
            startIcon={<BookOpen size={16} />}
            sx={{ whiteSpace: 'nowrap', alignSelf: { xs: 'stretch', md: 'center' } }}
          >
            Ver guia de Renda Fixa
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity='error' sx={{ mb: 1.5 }}>
          Não foi possível carregar o catálogo. Tente novamente.
        </Alert>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '280px minmax(0, 1fr)' },
          gap: 2,
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
        }}
      >
        {!isMobile && (
          <Paper
            component='section'
            aria-label='Filtros do screener'
            variant='outlined'
            sx={{
              p: 1.75,
              borderRadius: 1.5,
              position: 'sticky',
              top: 16,
              height: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              background: 'linear-gradient(180deg, hsl(var(--card)) 0%, hsl(var(--muted) / 0.24) 100%)',
              '& .MuiInputBase-root': { fontSize: '0.82rem' },
              '& .MuiInputLabel-root': { fontSize: '0.82rem' },
              '& .MuiTypography-caption': { fontSize: '0.72rem' },
            }}
          >
            <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1.5 }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 900 }}>
                Filtros
              </Typography>
              <Button size='small' startIcon={<RefreshCw size={14} />} onClick={clearFilters}>
                Limpar tudo
              </Button>
            </Stack>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                pr: 0.5,
                mr: -0.5,
              }}
            >
              {filterPanel}
            </Box>
          </Paper>
        )}

        <Box sx={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <Paper variant='outlined' sx={{ p: 0.75, mb: 1.25, minHeight: 42, borderRadius: 1.5 }}>
            <Stack direction='row' alignItems='center' gap={0.75} flexWrap='wrap'>
              <Typography variant='caption' sx={{ fontWeight: 800, mr: 0.5 }}>
                Filtros ativos:
              </Typography>
              {appliedFilterLabels.length ? (
                appliedFilterLabels.map((filter) => <Chip key={filter} size='small' label={filter} />)
              ) : (
                <Typography variant='caption' color='text.secondary'>
                  Nenhum filtro aplicado
                </Typography>
              )}
              <Button size='small' sx={{ ml: 'auto' }} onClick={clearFilters}>
                Limpar
              </Button>
            </Stack>
          </Paper>

          {response && rows.length === 0 ? (
            <Paper variant='outlined' sx={{ p: 4, textAlign: 'center', mb: 1.5 }}>
              <Typography>Nenhum título encontrado</Typography>
              <Typography variant='body2' color='text.secondary'>
                Ajuste os filtros para ampliar sua busca.
              </Typography>
            </Paper>
          ) : null}

          <Paper
            variant='outlined'
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: 'space-between',
              gap: 1,
              px: 1,
              py: 0.75,
              borderRadius: '6px 6px 0 0',
              borderBottom: 0,
            }}
          >
            <Stack
              direction='row'
              alignItems='center'
              spacing={0.5}
              role='tablist'
              aria-label='Visualização do catálogo'
            >
              {(
                [
                  ['cards', 'Cards', LayoutGrid],
                  ['table', 'Tabela', Table2],
                  ['charts', 'Gráfico', LineChart],
                ] as const
              ).map(([mode, label, Icon]) => (
                <Button
                  key={mode}
                  role='tab'
                  aria-selected={viewMode === mode}
                  variant={viewMode === mode ? 'contained' : 'text'}
                  size='small'
                  startIcon={<Icon size={15} />}
                  onClick={() => setViewMode(mode)}
                >
                  {label}
                </Button>
              ))}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
              {viewMode === 'table' && (
                <Button size='small' variant='outlined' onClick={() => setColumnsOpen(true)}>
                  Colunas
                </Button>
              )}
              <Stack direction='row' spacing={0.5} alignItems='center' justifyContent='center'>
                <Typography variant='caption' color='text.secondary' noWrap>
                  {selected.length === 0
                    ? 'Selecione títulos para comparar'
                    : selected.length === 1
                      ? '1 título selecionado · selecione mais 1'
                      : `${selected.length} de 4 títulos selecionados`}
                </Typography>
                <Tooltip title='Selecione até quatro títulos para habilitar a comparação.' arrow>
                  <Typography component='span' variant='caption' color='text.secondary' sx={{ cursor: 'help' }}>
                    ⓘ
                  </Typography>
                </Tooltip>
              </Stack>
              <Button
                component={AppLink}
                href={selected.length > 1 ? compareHref : '#resultados'}
                variant='contained'
                size='small'
                disabled={selected.length < 2}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Comparar selecionados ({selected.length})
              </Button>
              <FormControl size='small' sx={{ minWidth: { xs: 0, sm: 180 }, flex: { xs: 1, sm: 'initial' } }}>
                <InputLabel>Ordenar por</InputLabel>
                <Select
                  value={sort}
                  label='Ordenar por'
                  startAdornment={<ArrowUpDown size={14} style={{ marginRight: 6 }} />}
                  onChange={(event) => setSort(event.target.value as 'maturity' | 'rate')}
                >
                  <MenuItem value='maturity'>Vencimento próximo</MenuItem>
                  <MenuItem value='rate'>Maior taxa</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Paper>

          <Box
            id='resultados'
            sx={{
              minWidth: 0,
              minHeight: 0,
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              overflowX: viewMode === 'table' ? 'auto' : 'hidden',
              overflowY: 'hidden',
            }}
          >
            {viewMode === 'table' ? (
              <GridComponent
                key={`fixed-income-${hiddenColumns.join('-')}`}
                cssClass='fixed-income-grid'
                dataSource={response ? rows : []}
                allowSorting
                allowResizing
                enableStickyHeader
                width='100%'
                height={isMobile ? 'auto' : '100%'}
                rowHeight={isMobile ? 48 : 50}
                locale='pt'
              >
                <ColumnsDirective>
                  {gridColumns.map((column, index) => (
                    <ColumnDirective key={column.field ?? `select-${index}`} {...column} />
                  ))}
                </ColumnsDirective>
                <Inject services={[Sort, Resize]} />
              </GridComponent>
            ) : viewMode === 'cards' ? (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(2, minmax(0, 1fr))',
                      xl: 'repeat(3, minmax(0, 1fr))',
                    },
                    gap: 1.25,
                    p: 1,
                    borderRadius: 1.5,
                    bgcolor: 'hsl(var(--muted) / 0.42)',
                    flex: 1,
                    minHeight: 0,
                    height: '100%',
                    overflowY: 'auto',
                  }}
                >
                  {rows.map(renderCard)}
                </Box>
              </>
            ) : (
              renderCharts()
            )}
          </Box>
          {viewMode === 'charts' && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              alignItems='center'
              justifyContent='space-between'
              gap={1}
              sx={{ mt: 1.5 }}
            >
              <Typography variant='caption' color='text.secondary'>
                Dados do universo filtrado: {rows.length} títulos.
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                Fonte: Tesouro Transparente · Referência: {formatDate(quoteDate)}
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>

      <Drawer
        anchor='left'
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        PaperProps={{ sx: { width: 'min(92vw, 360px)', p: 1.75 } }}
      >
        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1.5 }}>
          <Typography variant='h6' fontWeight={800}>
            Filtros
          </Typography>
          <Button size='small' onClick={clearFilters}>
            Limpar tudo
          </Button>
        </Stack>
        {filterPanel}
      </Drawer>

      <Dialog open={columnsOpen} onClose={() => setColumnsOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle>Colunas da tabela</DialogTitle>
        <DialogContent dividers>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Mostre apenas as métricas úteis para a leitura atual.
          </Typography>
          <Stack spacing={0.75}>
            {columnOptions.map(([field, label]) => {
              const visible = !hiddenColumns.includes(field);
              return (
                <Button
                  key={field}
                  variant='outlined'
                  aria-pressed={visible}
                  onClick={() =>
                    setHiddenColumns((current) =>
                      visible ? [...current, field] : current.filter((item) => item !== field),
                    )
                  }
                  disabled={visible && hiddenColumns.length >= columnOptions.length - 1}
                  sx={{
                    justifyContent: 'space-between',
                    textTransform: 'none',
                    borderColor: visible ? 'primary.main' : 'divider',
                    bgcolor: visible ? 'action.selected' : 'transparent',
                  }}
                >
                  {label}
                  <Typography variant='caption'>{visible ? 'Visível' : 'Oculta'}</Typography>
                </Button>
              );
            })}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(selectedChartRow)} onClose={() => setSelectedChartRow(null)} fullWidth maxWidth='sm'>
        {selectedChartRow && (
          <>
            <DialogTitle>{friendlyTitle(selectedChartRow)}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5}>
                <Stack direction='row' justifyContent='space-between' alignItems='center' gap={1}>
                  <Box>
                    <Chip
                      size='small'
                      label={titleTag(selectedChartRow.titleType)}
                      sx={{ bgcolor: 'action.hover', fontWeight: 700 }}
                    />
                    <Typography variant='caption' display='block' color='text.secondary' sx={{ mt: 0.5 }}>
                      {instrumentCode(selectedChartRow)}
                    </Typography>
                  </Box>
                  <Typography variant='h6' fontWeight={800} textAlign='right'>
                    {formatRate(selectedChartRow) ?? <MissingValue />}
                  </Typography>
                </Stack>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      PU de referência
                    </Typography>
                    <Typography variant='body2' fontWeight={700}>
                      {selectedChartRow.sellPuMorning === null ? (
                        <MissingValue />
                      ) : (
                        formatPrice(selectedChartRow.sellPuMorning)
                      )}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Vencimento
                    </Typography>
                    <Typography variant='body2' fontWeight={700}>
                      {formatDate(selectedChartRow.maturityDate)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Duração
                    </Typography>
                    <Typography variant='body2'>{metric(selectedChartRow.durationYears, ' anos')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Indexador
                    </Typography>
                    <Typography variant='body2'>{formatEnum(selectedChartRow.indexer)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Liquidez
                    </Typography>
                    <LiquidityValue row={selectedChartRow} />
                  </Box>
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Tributação
                    </Typography>
                    <Typography variant='body2'>{formatEnum(selectedChartRow.taxRegime)}</Typography>
                  </Box>
                </Box>
                <Typography variant='caption' color='text.secondary'>
                  As taxas apresentadas têm bases diferentes entre indexadores e não representam recomendação.
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelectedChartRow(null)}>Fechar</Button>
              <Button
                component={AppLink}
                href={`/ativo/${encodeURIComponent(selectedChartRow.idCem)}`}
                variant='contained'
              >
                Ver detalhes completos
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
